import Dexie, { type EntityTable } from 'dexie'
import type { VocabularyEntry } from '@ghp/domain'

/**
 * Ce que la tablette garde et qui **n'est la saisie de personne** : le référentiel d'anomalies,
 * le journal de diagnostic et les réglages de l'appareil. Une base unique, partagée par toutes
 * les équipes, à côté des bases de tournée — une par équipe (`offline/data-db.ts`, D22 pt.4).
 *
 * Quatre tables, quatre raisons de ne pas être cloisonnées, et aucune n'est une commodité :
 *
 * - **le référentiel** (M17, M18) est remplacé en entier à chaque descente et n'appartient à
 *   aucune équipe. Le ranger par équipe le ferait redescendre à chaque changement de main pour
 *   rendre exactement les mêmes lignes — et sur une tablette hors réseau, le sélecteur
 *   d'anomalies resterait vide toute la tournée ;
 * - **le journal** (D15) est celui de la *tablette*, pas d'une équipe. Il s'ouvre au démarrage,
 *   **avant** que la session locale ne soit lue : le ranger par équipe laisserait sans trace
 *   exactement les incidents qu'on ne peut pas reproduire au bureau — ceux du démarrage — et
 *   couperait en morceaux un parcours qui traverse un changement d'équipe ;
 * - **les réglages** sont ceux de l'appareil, et le disaient déjà quand ils vivaient dans la
 *   base commune : c'est la tablette qui est réglée, pas la session ;
 * - **les règles du serveur** (M35) sont celles de GHP, les mêmes pour tous les binômes :
 *   redescendues avec la tournée de n'importe quelle équipe, elles valent pour toutes.
 *
 * ⚠️ **`ghp-terrain-shared`, et non `ghp-terrain-reference` comme le proposait #158** : le nom
 * a suivi ce qu'il y a dedans. Un journal de diagnostic n'est pas un référentiel, et une base
 * nommée pour la moitié de son contenu invite à ranger la seconde ailleurs — c'est-à-dire, ici,
 * dans une base d'équipe où elle se perdrait.
 *
 * ⚠️ Ne **jamais** y remettre une donnée de terrain. La ligne de partage est le geste de purge
 * qu'elle rend possible : effacer la base d'une équipe est sans risque pour une autre
 * (`indexedDB.deleteDatabase`), mais rien n'efface celle-ci sans emporter le travail de tout le
 * monde.
 */
export type SharedDatabase = Dexie & {
  vocabulary: EntityTable<VocabularyEntry, 'id'>
  logs: EntityTable<LogEntry, 'seq'>
  preferences: EntityTable<DevicePreferences, 'id'>
  serverRules: EntityTable<ServerRules, 'id'>
}

/**
 * Les réglages de **l'appareil** — ce que l'agent commute depuis l'écran « Réglages ».
 *
 * ⚠️ **Ni rangés par équipe, ni effacés par une déconnexion**, et les deux se déduisent du même
 * fait : c'est la tablette qui est réglée, pas la session. Le binôme qui prend la relève hérite
 * du GPS coupé dans le vide sanitaire où il travaille — le lui faire recouper à chaque passation
 * serait le réglage le plus vite abandonné de l'app. D'où sa place ici, avec le référentiel et
 * le journal, plutôt que dans la base d'une équipe.
 *
 * ⚠️ **Une seule ligne, clé fixe** — `put` écrase. Et **tous les champs sont facultatifs** : une
 * tablette qui n'a jamais ouvert l'écran n'a aucune ligne, et une ligne écrite avant qu'un
 * réglage n'existe ne le porte pas. C'est `readDevicePreferences` (`offline/preferences.ts`) qui
 * tranche l'absence, une seule fois, dans le sens du fonctionnement d'aujourd'hui — sans quoi
 * chaque lecteur inventerait son propre défaut, et une mise à jour de l'app éteindrait le GPS de
 * tout le parc.
 */
export interface DevicePreferences {
  id: 'current'
  /** Relever la position GPS à la signature (M20) — le geste, pas la permission du navigateur. */
  autoGeolocation?: boolean
  /** Laisser les passes d'arrière-plan vider la file d'envoi (D4). */
  autoSync?: boolean
}

/**
 * Ce que **l'organisation autorise**, redescendu avec la tournée et gardé pour le hors-réseau.
 *
 * ⚠️ **Ici et non dans la base d'une équipe**, comme le référentiel d'anomalies et pour la même
 * raison : c'est une règle de GHP, la même pour tous les binômes, et la saisie de personne.
 *
 * ⚠️ **Une seule ligne, remplacée en entier** : un réglage partiel n'aurait aucun sens, et un
 * `id` fixe évite d'avoir à balayer une table pour lire un booléen.
 *
 * ⚠️ **Le champ est facultatif, et c'est ce qui rend la mise à jour sûre** : une tablette qui n'a
 * pas encore resynchronisé n'a pas la ligne, et `readServerRules` tranche l'absence **une fois**,
 * dans le sens du fonctionnement d'aujourd'hui. Sans ce point unique, chaque lecteur inventerait
 * son défaut.
 */
export interface ServerRules {
  id: 'current'
  /** Joindre une pièce **après** avoir transmis un contrôle (M35). */
  allowAttachmentsAfterSubmission?: boolean
}

/**
 * Une ligne du journal local (`logs`) : ce que la tablette consigne pour le diagnostic —
 * erreurs JavaScript, appels API, navigation, passes de synchro, gestes marquants.
 *
 * C'est un **tampon circulaire**, pas une archive : la table garde tout le journal jusqu'au
 * plafond de `offline/logger.ts`, et **seule** la taille du tampon retire des lignes — les
 * déjà montées d'abord. L'expédition, elle, ne supprime rien : elle **marque** ({@link
 * LogEntry.shipped}), et le prochain lot se lit sur cet index sans balayer le tampon. Deux
 * chemins de sortie : l'expédition automatique par lots vers `POST /sync/logs` (table
 * `client_log`, requêtable en SQL), et l'export local de l'écran Notifications — qui rend
 * **tout** le journal encore présent, monté compris, pour le hors-réseau total (D15).
 */
export interface LogEntry {
  /** Clé auto-incrémentée par Dexie : l'ordre d'écriture est l'ordre de lecture. */
  seq?: number
  at: string
  /** L'uuid v7 tiré au démarrage de l'app — c'est lui qui recoud un parcours entier. */
  sessionId: string
  level: 'info' | 'warn' | 'error'
  /** Le préfixe de convention des consoles du dépôt : `[app]`, `[sync]`, `[api]`, `[js]`… */
  tag: string
  /** Le nom machine stable de l'événement (`app.boot`, `sync.sent`, `api.call`…). */
  event: string
  message: string
  /** Le contexte chiffré de l'événement — sérialisable, déjà borné par le logger. */
  detail: unknown
  /** Le contrôle concerné, quand il y en a un. */
  inquiryId: string | null
  /**
   * `0` tant que la ligne reste à monter, `1` quand elle n'a plus à partir — montée au
   * serveur, **ou** mise de côté sur un refus de contenu. Les deux sens ne se distinguent pas
   * ici, et c'est assez : l'index ne répond qu'à « reste-t-il à envoyer ? », et l'abandon est
   * tracé en clair par une ligne `sync.logs_dropped`. Si compter les lignes réellement reçues
   * devenait un besoin, la réponse serait un troisième état, pas une relecture du `1`.
   *
   * Deux valeurs et non un horodatage `null`able : IndexedDB n'accepte comme clé d'index ni
   * `null`, ni `undefined`, ni un booléen — un enregistrement dont le keyPath vaut l'un des
   * trois est simplement **absent** de l'index. « Pas encore expédié » exprimé par un `null`
   * serait donc inindexable, et chaque passe balaierait le tampon entier.
   *
   * ⚠️ Ne voyage **jamais** sur le réseau : `toClientLogEntry` (`offline/logger.ts`) énumère
   * ses champs un à un. Ne jamais le remplacer par un spread — le contrat d'ingestion est un
   * `looseObject` (`@ghp/domain`) : il accepterait ce champ **sans un mot**, aucun lot ne
   * serait refusé, et personne ne verrait la fuite. Ce que le serveur en fait ne rachète rien —
   * `sync/logs.controller.ts` énumère ses colonnes et `client_log` n'a pas d'état d'envoi, donc
   * la valeur se perdrait en route après avoir pesé sur le budget d'octets du lot. L'état
   * d'envoi est la question de la tablette ; le seul chemin par lequel il sort d'ici est
   * l'export local, où il est utile.
   */
  shipped: 0 | 1
}

/**
 * ⚠️ Base **distincte de `ghp-terrain-session`** comme des bases de tournée : elle survit à une
 * déconnexion et à un changement d'équipe, parce que ni le journal de la tablette ni le
 * référentiel d'anomalies n'appartiennent à celui qui vient de rendre l'écran.
 */
export const SHARED_DATABASE_NAME = 'ghp-terrain-shared'

/**
 * Fabrique, pas singleton — même raison que partout ailleurs sur cette couche : une instance
 * partagée ferait fuiter l'état d'un test au suivant.
 *
 * ⚠️ **`version(1)`**, bien que ces tables aient une histoire : elles vivaient dans la base
 * commune (`ghp-terrain-data`), que rien ne reprend — une base neuve n'a pas de version
 * antérieure (`offline/retired-data.ts`).
 */
export function openSharedDatabase(): SharedDatabase {
  const db = new Dexie(SHARED_DATABASE_NAME) as SharedDatabase

  db.version(1).stores({
    vocabulary: 'id, list',
    logs: '++seq, shipped',
    preferences: 'id',
    serverRules: 'id',
  })

  return db
}
